import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

/**
 * Store global de jobs en curso (M2.27).
 *
 * Problema que resuelve: las generaciones del coach IA y nutricionista
 * tardan 30-90 segundos (claude_code). Si el usuario navega durante ese
 * tiempo, el spinner del form desaparece y no hay forma de saber que el
 * backend sigue trabajando. El plan se persiste (preview=false desde
 * M2.26) pero el usuario no tiene feedback.
 *
 * Este store vive en el árbol root (envolvemos `RouterProvider` con
 * `JobsProvider`) y sobrevive a cualquier navegación. Los forms registran
 * un job al arrancar la mutation; `JobsBanner` (en AppLayout) lo pinta como
 * pill flotante en la esquina inferior-derecha con el tiempo transcurrido
 * en vivo. Al terminar pasa a estado `done` con link a la página de
 * resultados y se auto-descarta tras 30 s.
 */

export type JobStatus = 'running' | 'done' | 'error';

export type JobType = 'coach-plan' | 'meal-plan';

export interface Job {
  id: string;
  type: JobType;
  /** Texto corto a pintar: "Coach IA · semana del 1 jun". */
  label: string;
  status: JobStatus;
  startedAt: number;
  /** Solo poblado cuando `status === 'done' | 'error'`. */
  finishedAt: number | null;
  /** Mensaje de error (status=error). */
  errorMessage: string | null;
  /** Ruta a navegar al hacer click cuando done (ej. `/entrenos`). */
  resultPath: string | null;
}

interface JobsContextValue {
  jobs: Job[];
  startJob: (input: {
    type: JobType;
    label: string;
    /** Promise de la operación (típicamente `mutateAsync`). */
    promise: Promise<unknown>;
    /** Ruta donde el usuario verá el resultado. */
    resultPath?: string;
  }) => string;
  dismissJob: (id: string) => void;
}

const JobsContext = createContext<JobsContextValue | null>(null);

let counter = 0;
function nextId(): string {
  counter += 1;
  return `job-${Date.now().toString(36)}-${counter}`;
}

/** TTL después de `done` antes de auto-desaparecer (ms). */
const AUTO_DISMISS_MS = 30_000;

export function JobsProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [jobs, setJobs] = useState<Job[]>([]);

  const startJob: JobsContextValue['startJob'] = useCallback(
    ({ type, label, promise, resultPath }) => {
      const id = nextId();
      const job: Job = {
        id,
        type,
        label,
        status: 'running',
        startedAt: Date.now(),
        finishedAt: null,
        errorMessage: null,
        resultPath: resultPath ?? null,
      };
      setJobs((prev) => [...prev, job]);

      promise.then(
        () => {
          setJobs((prev) =>
            prev.map((j) =>
              j.id === id
                ? { ...j, status: 'done', finishedAt: Date.now() }
                : j,
            ),
          );
        },
        (err: unknown) => {
          setJobs((prev) =>
            prev.map((j) =>
              j.id === id
                ? {
                    ...j,
                    status: 'error',
                    finishedAt: Date.now(),
                    errorMessage:
                      err instanceof Error ? err.message : String(err),
                  }
                : j,
            ),
          );
        },
      );

      return id;
    },
    [],
  );

  const dismissJob = useCallback((id: string) => {
    setJobs((prev) => prev.filter((j) => j.id !== id));
  }, []);

  // Auto-dismiss jobs `done` después de AUTO_DISMISS_MS. Para `error` no
  // auto-dismiss: el usuario debe leerlo y cerrarlo manualmente.
  useEffect(() => {
    const doneIds = jobs
      .filter((j) => j.status === 'done' && j.finishedAt !== null)
      .map((j) => ({ id: j.id, finishedAt: j.finishedAt! }));
    if (doneIds.length === 0) return;

    const timers = doneIds.map(({ id, finishedAt }) => {
      const elapsed = Date.now() - finishedAt;
      const remaining = Math.max(0, AUTO_DISMISS_MS - elapsed);
      return window.setTimeout(() => dismissJob(id), remaining);
    });
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [jobs, dismissJob]);

  const value = useMemo<JobsContextValue>(
    () => ({ jobs, startJob, dismissJob }),
    [jobs, startJob, dismissJob],
  );

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>;
}

/**
 * Fallback no-op cuando se invoca `useJobs` fuera de `<JobsProvider>`.
 * Lo usamos en tests (renderWithRouter no envuelve por defecto) y como
 * red de seguridad: si alguien renderiza un form fuera del árbol root
 * el banner global simplemente no recibe el job en vez de crashear el
 * árbol entero. `App.tsx` SÍ monta el provider, así que en producción
 * el path real siempre tiene contexto.
 */
const NOOP_JOBS: JobsContextValue = {
  jobs: [],
  startJob: () => 'noop',
  dismissJob: () => undefined,
};

export function useJobs(): JobsContextValue {
  const ctx = useContext(JobsContext);
  return ctx ?? NOOP_JOBS;
}
